// ── PhonePe return page ─────────────────────────────────────────────────────────
// Where PhonePe sends the browser back after the payer finishes (or abandons) a checkout.
//
// The single most important thing about this screen: **it decides nothing.** The return is a plain
// browser navigation with no signature on it — a payer could type this URL themselves. All it does
// is call confirmPhonePePayment, which asks PhonePe server-to-server what actually happened and
// fulfils on the strength of that. So the states below are reported, never assumed.
//
// PENDING is normal, not an error: UPI collect requests can settle after the payer is redirected
// back, so we poll a few times before telling anyone to check later. The webhook is the backstop —
// even if this tab is closed mid-poll, the payment still completes.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { CONFIRM_PHONEPE_PAYMENT_MUTATION } from '../graphql';
import { useAuth } from '../context/AuthContext';

const POLL_ATTEMPTS = 6;      // ~30s total, then hand it to the webhook and stop spinning
const POLL_INTERVAL_MS = 5000;

export default function PhonePeReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const merchantOrderId = params.get('order') || '';

  const [state, setState] = useState('CHECKING');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const attempts = useRef(0);
  const timer = useRef(null);
  const [confirmPayment] = useMutation(CONFIRM_PHONEPE_PAYMENT_MUTATION);

  const check = useCallback(async () => {
    try {
      const { data } = await confirmPayment({ variables: { merchantOrderId } });
      const r = data?.confirmPhonePePayment;
      setResult(r);
      if (r?.state === 'COMPLETED') { setState('COMPLETED'); return; }
      if (r?.state === 'FAILED') { setState('FAILED'); return; }
      if (r?.state === 'UNDERPAID' || r?.state === 'UNKNOWN') { setState(r.state); return; }
      // Still PENDING — try again, up to a point.
      attempts.current += 1;
      if (attempts.current >= POLL_ATTEMPTS) { setState('PENDING'); return; }
      timer.current = setTimeout(check, POLL_INTERVAL_MS);
    } catch (e) {
      setError(e.message);
      setState('ERROR');
    }
  }, [confirmPayment, merchantOrderId]);

  useEffect(() => {
    if (!merchantOrderId) { setState('NO_ORDER'); return; }
    if (!currentUser) return;  // the mutation is owner-scoped; wait for the session to rehydrate
    check();
    return () => clearTimeout(timer.current);
  }, [merchantOrderId, currentUser, check]);

  // Where "continue" should take them, based on what they were buying.
  const goOn = () => {
    if (result?.entityType === 'GROUP_JOIN' && result?.entityId) navigate(`/group/${result.entityId}`);
    else if (result?.entityType === 'PRODUCT') navigate('/orders');
    else navigate('/');
  };

  const rupees = result?.amountPaise != null ? (result.amountPaise / 100).toFixed(2) : null;

  const views = {
    CHECKING: { icon: '⏳', title: 'Confirming your payment…', body: 'Checking with PhonePe. This takes a few seconds — please don’t close this page.' },
    COMPLETED: { icon: '✅', title: 'Payment confirmed', body: result?.fulfilled === false
      ? 'Your payment went through. We’re still setting things up — if it doesn’t appear shortly, contact the organiser and quote the reference below.'
      : 'All done. Your payment went through and everything is set up.' },
    PENDING: { icon: '🕓', title: 'Still processing', body: 'PhonePe hasn’t confirmed this one yet. You can safely close this page — we’ll finish it automatically the moment it settles, and you’ll get a notification.' },
    FAILED: { icon: '❌', title: 'Payment did not go through', body: 'PhonePe reports this payment as failed. Nothing has been charged that we can see — you can try again.' },
    UNDERPAID: { icon: '⚠️', title: 'Amount doesn’t match', body: 'The amount received is less than the price of this order, so it hasn’t been completed. Contact the organiser with the reference below.' },
    UNKNOWN: { icon: '⚠️', title: 'We can’t find that order', body: 'This payment reference doesn’t match anything we created. If money left your account, contact support with the reference below.' },
    NO_ORDER: { icon: '🤔', title: 'Nothing to confirm', body: 'This link is missing a payment reference. If you were paying for something, open it again from the group or shop.' },
    ERROR: { icon: '⚠️', title: 'Couldn’t check the payment', body: error || 'Something went wrong while confirming.' },
  };
  const v = views[state] || views.CHECKING;

  return (
    <div className="page">
      <div className="container" style={{ paddingTop: 90, maxWidth: 480 }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>{v.icon}</div>
          <h2 style={{ fontSize: 'var(--font-lg)', fontWeight: 800, marginBottom: 8 }}>{v.title}</h2>
          {rupees && <div style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: 8 }}>₹{rupees}</div>}
          <p style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.6 }}>{v.body}</p>

          {state === 'CHECKING' && <div className="loading-center"><div className="spinner" /></div>}

          {merchantOrderId && (
            <div style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'monospace', wordBreak: 'break-all', marginBottom: 16 }}>
              Ref: {merchantOrderId}
            </div>
          )}

          {state !== 'CHECKING' && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-primary btn-sm" onClick={goOn}>
                {state === 'COMPLETED' ? 'Continue' : 'Back to the app'}
              </button>
              {(state === 'PENDING' || state === 'ERROR') && (
                <button className="btn btn-ghost btn-sm" onClick={() => { attempts.current = 0; setState('CHECKING'); check(); }}>
                  Check again
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
